//React
import * as React from "react";
import { connect } from "react-redux";

//Store
import { MainStore } from "./../../store/MainStore";

//React Components
import { Paper, Typography, IconButton, Button } from "material-ui";
import Phone from "material-ui-icons/Phone";
import {
    FilteringState,
    IntegratedFiltering,
    RowDetailState,
    TableColumnVisibility,
    SortingState,
    PagingState,
    IntegratedPaging,
    IntegratedSorting
} from "@devexpress/dx-react-grid";
import { Grid, Table, TableRowDetail, PagingPanel } from "@devexpress/dx-react-grid-material-ui";
import { EmptyMessage } from "../components/templates/EmptyMessage";

//Icons
import FiberManualRecord from "material-ui-icons/FiberManualRecord";
import Loop from "material-ui-icons/Loop";

//Managers
import { StyleManager } from "./../../managers/StyleManager";
import { MethodRequest_e, NetworkManager } from "./../../managers/NetworkManager";

// Framework
import { Product } from "../../framework/Product";

//Style
const styles = StyleManager.getStyles().MaterialsView;

/**
 * @description Custom TableCell with specific style for the Table
 * @author Lucas Fridez <lfridez@artionet.com>
 * @param {*} props
 */
const TableCell = (props) => {
    return (<Table.Cell {...props} style={styles.customTableCell} />);
};

const setDetailRowExpanded = (prevExpanded, rowId) => {
    const expandedRows = prevExpanded.slice();
    const expandedIndex = expandedRows.indexOf(rowId);

    if (expandedIndex > -1) {
        expandedRows.splice(expandedIndex, 1);
    } else if (expandedIndex === -1) {
        expandedRows.push(rowId);
    }

    return expandedRows;
};

/**
 * @description Row Detail which contain all the detail when a row is clicked
 * @author Lucas Fridez <lfridez@artionet.com>
 * @param {*} param
 */
const RowDetail = ({ row }) => {
    const product = new Product(row);
    return (
        <div style={styles.rowDetail}>
            {product.borrowed != false
                ? <div style={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: 16,
                    marginTop: 6
                }}>
                    <Typography type="body1">
                        <strong>Emprunté par</strong>
                        : {product.owner}
                    </Typography>
                    <a href={`tel:${product.phone}`} style={styles.phoneButton}>
                        <Button raised fab mini color="primary" style={styles.phoneButtonFab}>
                            <Phone style={styles.miniIcon} />
                        </Button>
                    </a>
                </div>
                :
                <Typography type="body1">
                    <strong>Entrepôt</strong>
                    : {product.warehouse}
                </Typography>}
            <Typography type="body1" gutterBottom style={{ marginBottom: 16 }}>
                <strong>Description</strong>
                : {product.description}
            </Typography>
            {product.image != "" &&
                <img src={product.image} alt={`Photo principale ${product.name}`} style={{
                    width: "100%"
                }} />
            }
            <Typography type="body1" gutterBottom>
                <strong>Dommages</strong> : {product.damages.length == 0 ? "Aucun dommage sur ce matériel." : ""}
            </Typography>
            {product.damages.length > 0 &&
                <ul style={{margin: "5px 0", paddingLeft: "30px"}}>
                {product.damages.map((damage) => (
                    <li><Typography type="body1" gutterBottom>{damage.text_dam}</Typography></li>
                ))}
                </ul>
            }
        </div>
    );
};

const TableDetailToggleCellBase = ({
    style,
    expanded,
    classes,
    onToggle,
    tableColumn,
    tableRow,
    row,
    className,
    ...restProps
}) => {
    const toggleCellStyle = row.borrowed === "1" ? "red" : "green";
    if (row.borrowed === "1") {
        if (row.validated_borrow == false) {
            return (
                <Table.Cell {...restProps} style={styles.toggleCell}>
                    <IconButton><Loop /></IconButton>
                </Table.Cell>
            );
        }
    }
    return (
        <Table.Cell {...restProps} style={styles.toggleCell}>
            <IconButton><FiberManualRecord style={toggleCellStyle === "green" ? styles.greenIcon : styles.redIcon} /></IconButton>
        </Table.Cell>
    );
};

/**
 * Column of the DataTable
 * @author Lucas Fridez <lfridez@artionet.com>
 */
const columns = [
    {
        name: "name",
        title: "Produit"
    }
];

interface Props {
    categories: any;
    location: string;
    productListing?: any;
    filters?: any;
    sorting?: any;
}

interface State {
    expandedRows: any;
    filters: any;
    sorting?: any;
    products: any;
}

/**
 * @description Contain the Materials View
 * @author Lucas Fridez <lfridez@artionet.com>
 * @export
 * @class Materials
 * @extends {React.Component}
 */
export default connect<{}, {}, Props>((state) => state)(class DataTable extends React.Component<Props, State> {

    changeExpandedDetails = null;

    /**
     * Creates an instance of Materials.
     * @author Lucas Fridez <lfridez@artionet.com>
     * @param {any} props
     * @memberof Materials
     */
    constructor(props) {
        super(props);
        this.state = {
            expandedRows: [],
            filters: "",
            products: []
        };
        this.changeExpandedDetails = (expandedRows) => this.setState({ expandedRows });
    }

    /**
     * @description Component DataTable will be mounted in the App
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof DataTable
     */
    async componentWillMount() {
        MainStore.getProductListing();
    }

    /**
     * @description Component DataTable will receive new props (for category filtering)
     * @author Lucas Fridez <lfridez@artionet.com>
     * @param {any} nextProps
     * @returns {boolean} false if nothin changed; true if the filters have changed
     * @memberof DataTable
     */
    async componentWillReceiveProps(nextProps) {
        if (this.props.categories == nextProps.categories && this.props.location == nextProps.location) {
            return false;
        } else {
            MainStore.getProductListing(`categories=${encodeURIComponent(JSON.stringify(nextProps.categories))}`, encodeURIComponent(nextProps.location));
            return true;
        }
    }

    /**
     * @description Change the sort of the table
     * @author Lucas Fridez <lfridez@artionet.com>
     * @memberof Materials
     */
    changeSorting = (sorting) => this.setState({ sorting } as any);

    /**
     * @description Render the Materials View
     * @author Lucas Fridez <lfridez@artionet.com>
     * @returns Materials View
     * @memberof Materials
     */
    render() {
        const expandedRows = this.state.expandedRows;
        const rowComponent = ({ tableRow, children }) => {
            return (
                <Table.Row
                    style={{ borderBottom: "1px solid #eee" }}
                    onClick={() =>
                        this.changeExpandedDetails(
                            setDetailRowExpanded(expandedRows, tableRow.rowId)
                        )}>
                    {children}
                </Table.Row>
            );
        };

        return (
            <Paper id="products-table">
                <Grid rows={this.props.productListing.productListing} columns={columns}>
                    <FilteringState filters={this.props.filters} />
                    <IntegratedFiltering />
                    <RowDetailState
                        expandedRows={expandedRows}
                        onExpandedRowsChange={this.changeExpandedDetails}
                    />
                    <SortingState
                        sorting={this.state.sorting}
                        onSortingChange={this.changeSorting} />
                    <IntegratedSorting />
                    <PagingState
                        defaultCurrentPage={0}
                        pageSize={5}
                    />
                    <IntegratedPaging />
                    <Table
                        cellComponent={TableCell}
                        rowComponent={rowComponent}
                        messages={{
                            noData: "Aucun produit ne correspond à vos critères."
                        }} />
                    <TableColumnVisibility
                        emptyMessageComponent={EmptyMessage}
                        defaultHiddenColumns={["place", "owner", "description", "img"]} />
                    <TableRowDetail contentComponent={RowDetail} toggleCellComponent={TableDetailToggleCellBase} />
                    <PagingPanel messages={{ info: "{from}-{to} sur {count}" }} />
                </Grid>
            </Paper>
        );
    }
});



// WEBPACK FOOTER //
// ./src/app/UI/components/DataTable.tsx